<template>
  <div
    class="ol-callout"
    data-ui="ol-callout"
  >
    <div class="ol-callout__image-container">
      <picture v-if="image">
        <source
          media="(-webkit-min-device-pixel-ratio: 1.5)"
          :srcset="srcsetMain"
          :sizes="sizes"
        >
        <img
          :srcset="srcsetFallback"
          :alt="image?.alt"
          :aria-hidden="!image?.alt"
          :sizes="sizes"
          loading="lazy"
          class="ol-callout__image"
        >
      </picture>
    </div>
    <div
      class="ol-callout__info"
      data-analytics-config="exclude-all"
    >
      <h2
        v-if="heading"
        data-ui="ol-callout-heading"
        class="ol-callout__heading"
        v-html="heading"
      />
      <p
        v-if="body"
        class="ol-callout__body"
        v-html="body"
      />
      <cdr-button
        v-if="cta"
        :href="cta?.target"
        class="ol-callout__cta"
        modifier="primary"
        tag="a"
        @click="handleCtaClick"
      >
        {{ cta?.text }}
      </cdr-button>
    </div>
  </div>
</template>

<script setup>
import { computed } from 'vue';
import { CdrButton } from '@rei/cedar';
import { metricsLink } from '../../../utils/analytics';

const props = defineProps({
  body: {
    type: String,
    required: false,
    default: '',
  },
  cta: {
    type: Object,
    required: false,
    default: null,
  },
  heading: {
    type: String,
    required: false,
    default: '',
  },
  image: {
    type: Object,
    required: false,
    default: null,
  },
});

/* c8 ignore start */
const handleCtaClick = () => {
  metricsLink(`${props?.heading}_${props?.cta?.text}`);
};
/* c8 ignore end */

const srcsetMain = computed(() => {
  const { image } = props;
  return `
    ${image.src}?im=Resize,width=2000 2000w,
    ${image.src}?im=Resize,width=1400 1400w,
    ${image.src}?im=Resize,width=800 800w
  `;
});

const srcsetFallback = computed(() => {
  const { image } = props;
  return `
    ${image.src}?im=Resize,width=900&density=1 900w,
    ${image.src}?im=Resize,width=700&density=1 700w
  `;
});

const sizes = computed(() => '(min-width: 1024px) 700px, 95vw');
</script>

<style lang="scss">
@import '../../../../style/common/paths.scss';

.ol-callout {
  width: 100%;
  position: relative;
  margin: $cdr-space-two-x auto 0;

  @include cdr-md-mq-up {
    display: grid;
    padding-right: $cdr-space-two-x;
    padding-left: $cdr-space-two-x;
    max-width: $cdr-breakpoint-lg;
    margin-top: $cdr-space-three-x;
  }
  @include cdr-lg-mq-up {
    margin-top: $cdr-space-four-x;
  }
}

.ol-callout__info,
.ol-callout__image-container {
  @include cdr-md-mq-up {
    grid-row: 1;
    grid-column: 1;
  }
}

.ol-callout__info {
  background-color: $cdr-color-background-secondary;
  border-bottom-left-radius: $cdr-radius-softer;
  border-bottom-right-radius: $cdr-radius-softer;
  padding: $cdr-space-one-x $cdr-space-one-x $cdr-space-one-and-a-half-x;
  width: 100%;
  position: relative;

  @include cdr-md-mq-up {
    justify-self: start;
    align-content: center;
  }

  &::before {
    content: '';
    background-color: $cdr-color-background-secondary;
    border-top-left-radius: $cdr-radius-softer;
    border-top-right-radius: $cdr-radius-softer;
    display: block;
    width: 100%;
    height: $cdr-space-three-x;
    position: absolute;
    bottom: 100%;
    left: 0;
    right: 0;
    @include cdr-md-mq-up {
      display: none;
    }
  }

  @include cdr-md-mq-up {
    max-width: 445px;
    min-height: 284px;
    align-self: center;
    padding: $cdr-space-two-x $cdr-space-one-and-a-half-x;
    border-radius: $cdr-radius-softer;
  }
}

.ol-callout__image-container {
  position: relative;
  width: calc(100vw - $cdr-space-two-x);
  min-height: 240px;
  margin: 0 auto;
  z-index: 1;
  @include cdr-md-mq-up {
    height: auto;
    max-height: 500px;
    width: 85%;
    max-width: 802px;
    margin: 0;
    z-index: 0;
    justify-self: end;
  }
}

.ol-callout__image {
  width: 100%;
  height: 100%;
  border-radius: $cdr-radius-softer;
  object-fit: contain;
  aspect-ratio: 1.61;
  @include cdr-md-mq-up {
    border-radius: initial;
  }
}

.ol-callout__heading {
  @include cdr-text-heading-serif-600;
  font-weight: 500;
  margin: 0 0 $cdr-space-three-quarter-x;

  @include cdr-md-mq-up {
    @include cdr-text-heading-serif-800;
  }
}

.ol-callout__body {
  margin-top: $cdr-space-half-x;
  @include cdr-text-body-300;
}

.ol-callout__cta {
  width: 100%;
  justify-content: center;
}
</style>
